import HBButton from "@/components/HBButton";
import HBIcon from "@/components/HBIcon";
import { OperationType } from "@/types/inspirationWords";
import { openMaterialDownload } from "@/utils/applink";
import { saveDownloadClickCount } from "@/utils/download";
import DownloadProcess from "@/utils/downloadProcess";
import { isFont } from "@/utils/pinMediaType";
import { Dropdown, Menu } from "antd";
import { useMemo, useState } from "react";
import { When } from "react-if";
import CreativeExtensionButton from "./CreativeExtensionButton";
import styles from "./index.module.less";

export default function MaterialButton({
  pin,
  searchWord,
  showEditOnline,
  recordOperation,
  trackerData,
}: {
  pin: IPin;
  searchWord?: string;
  showEditOnline: boolean;
  recordOperation: (fileId: string, type: OperationType) => void;
  trackerData?: Record<string, any>;
}) {
  const info = pin.material_info;
  const material = pin.file_material;

  const files = useMemo(() => {
    let files = [];

    if (
      material.material_category === "平面模板" ||
      material.material_category === "视频模板"
    ) {
      const file = info?.files?.find(
        (file: any) => file.file_type === "ORIGIN"
      );
      if (file) {
        files.push(file);
      }
    } else if (material.material_category === "视频") {
      const mps = info?.content?.mps?.find((mps: any) => {
        return mps.name === "HD";
      });
      if (mps) {
        files.push({
          file_format: mps.file_format,
          file_size: mps.file_size,
          file_type: mps.name,
        });
      }
    } else if (isFont(pin)) {
      // 字体素材：优先显示 ttf 格式，如果没有则显示第一个
      const allFiles = info?.files || [];
      const ttfFile = allFiles.find(
        (file: any) => file.file_format?.toLowerCase() === "ttf"
      );
      if (ttfFile) {
        files = [ttfFile];
      } else if (allFiles.length > 0) {
        files = [allFiles[0]];
      }
    } else {
      files = info?.files;
    }

    if (files?.length > 1) {
      // 根据size排序
      files = files.sort((a: any, b: any) => a.file_size - b.file_size);
    }

    return files;
  }, [info, material]);

  // 判断是否为字体素材
  const isFontMaterial = useMemo(() => {
    return isFont(pin);
  }, [pin]);

  const [dropdownVisible, setDropdownVisible] = useState(false);

  // 处理下载逻辑
  const handleDownload = (file: any, index: number) => {
    if (pin?.file_id && recordOperation) {
      recordOperation(String(pin.file_id), OperationType.DOWNLOAD);
    }

    // 字体素材打开 applink
    if (isFontMaterial) {
      const materialId =
        pin?.material_info?.id || pin?.file_material?.material_id;
      if (materialId) {
        openMaterialDownload(materialId);
      }
      return;
    }

    const downloadProcess = new DownloadProcess({
      pin,
      file,
      searchWord,
    });
    const { count } = saveDownloadClickCount();
    downloadProcess.processDownloadMaterial(count);
  };

  // 获取素材ID
  const materialId = pin?.material_info?.id || pin?.file_material?.material_id;
  const buttonName = isFontMaterial ? "获取本地使用" : "下载";

  // 如果只有一个文件，保持原有逻辑
  if (!files || files.length <= 1) {
    const file = files?.[0];
    if (!file) return null;

    const format = file.file_format?.toUpperCase();
    // const size = Number(file.file_size);

    return (
      <div className={styles.btns}>
        <HBButton
          className={styles.downloadBtn}
          onClick={() => handleDownload(file, 0)}
          data-gd-click="button_click"
          data-gd-expose="button_expose"
          data-button-name={buttonName}
          data-module-name="素材信息"
          data-content-source={`1_${buttonName}${format}`}
          data-search-word={searchWord}
          data-pin-detail
          data-content-id={pin?.pin_id}
          data-board-id={pin?.board_id}
          data-file-id={pin?.file_id}
          data-material-id={materialId}
          {...(isFontMaterial && { "data-content-type": "字体" })}
          data-sub-page-nm="素材采集"
          data-iscopyright={1}
          theme="black"
          bold
          {...trackerData}
        >
          {buttonName}&nbsp;
          <When condition={!isFontMaterial}>
            <span>{format}</span>
            {/* <span className={styles.size}>
              {size ? `${(size / (1024 * 1024)).toFixed(2)}M` : ""}
            </span> */}
          </When>
        </HBButton>
        <CreativeExtensionButton pin={pin} searchWord={searchWord} />
        {showEditOnline && (
          <HBButton
            className={styles.editBtn}
            data-gd-click="button_click"
            data-gd-expose="button_expose"
            data-button-name="在线编辑"
            data-module-name="素材信息"
            data-content-category={
              pin?.material_info?.categories?.includes("模板")
                ? "模板"
                : "元素材"
            }
            data-search-word={searchWord}
            data-pin-detail
            {...trackerData}
            onClick={() => {
              const downloadProcess = new DownloadProcess({
                pin,
                searchWord,
              });
              downloadProcess.processPinDetailEditOnline();
            }}
            theme="gray"
            icon={<HBIcon type="edit_new" />}
          >
            <span className={styles.editBtnText}>编辑</span>
          </HBButton>
        )}
      </div>
    );
  }

  // 多个文件时，使用下拉菜单
  const firstFile = files[0];
  const firstFormat = firstFile.file_format?.toUpperCase();
  // const firstSize = Number(firstFile.file_size);
  const otherFiles = files.slice(1);

  // 构建下拉菜单
  const menuItems = otherFiles.map((file: any, index: number) => {
    const format = file.file_format?.toUpperCase();
    // const size = Number(file.file_size);
    const itemIndex = index + 1;
    return {
      key: itemIndex,
      label: (
        <div
          className={styles.dropdownItem}
          onClick={() => {
            handleDownload(file, itemIndex);
            setDropdownVisible(false);
          }}
          data-gd-click="button_click"
          data-gd-expose="button_expose"
          data-button-name={buttonName}
          data-module-name="素材信息"
          data-content-source={`${
            files.length - itemIndex
          }_${buttonName}${format}`}
          data-search-word={searchWord}
          data-pin-detail
          data-content-id={pin?.pin_id}
          data-board-id={pin?.board_id}
          data-file-id={pin?.file_id}
          data-material-id={materialId}
          {...(isFontMaterial && { "data-content-type": "字体" })}
          data-sub-page-nm="素材采集"
          data-iscopyright={1}
        >
          {buttonName}&nbsp;
          <When condition={!isFontMaterial}>
            <span>{format}</span>
            {/* <span className={styles.size}>
              {size ? `${(size / (1024 * 1024)).toFixed(2)}M` : ""}
            </span> */}
          </When>
        </div>
      ),
    };
  });

  const menu = <Menu items={menuItems} />;

  return (
    <div className={styles.btns}>
      <Dropdown
        overlay={menu}
        open={dropdownVisible}
        onOpenChange={setDropdownVisible}
        placement="bottomLeft"
        trigger={["click"]}
        getPopupContainer={(triggerNode) =>
          (triggerNode.parentElement || document.body) as HTMLElement
        }
      >
        <div className={styles.downloadBtnWrapper}>
          <HBButton
            className={styles.downloadBtn}
            onClick={(e) => {
              // 如果点击的是箭头区域，不执行下载，让 Dropdown 处理
              const target = e.target as HTMLElement;
              if (
                target.closest(`.${styles.dropdownArrow}`) ||
                target.closest(`.${styles.arrowContainer}`)
              ) {
                return;
              }
              // 点击按钮主体区域，直接下载
              handleDownload(firstFile, 0);
            }}
            data-gd-click="button_click"
            data-gd-expose="button_expose"
            data-button-name={buttonName}
            data-module-name="素材信息"
            data-content-source={`${files.length}_${buttonName}${firstFormat}`}
            data-search-word={searchWord}
            data-pin-detail
            data-content-id={pin?.pin_id}
            data-board-id={pin?.board_id}
            data-file-id={pin?.file_id}
            data-material-id={materialId}
            {...(isFontMaterial && { "data-content-type": "字体" })}
            data-sub-page-nm="素材采集"
            data-iscopyright={1}
            theme="black"
            bold
            {...trackerData}
          >
            {buttonName}&nbsp;
            <When condition={!isFontMaterial}>
              <span>{firstFormat}</span>
              {/* <span className={styles.size}>
                {firstSize ? `${(firstSize / (1024 * 1024)).toFixed(2)}M` : ""}
              </span> */}
            </When>
            <span className={styles.arrowContainer}>
              <HBIcon
                type="ic_arrow_drop_down"
                className={styles.dropdownArrow}
              />
            </span>
          </HBButton>
        </div>
      </Dropdown>
      <CreativeExtensionButton pin={pin} searchWord={searchWord} />
      {showEditOnline && (
        <HBButton
          className={styles.editBtn}
          data-gd-click="button_click"
          data-gd-expose="button_expose"
          data-button-name="在线编辑"
          data-module-name="素材信息"
          data-content-category={
            pin?.material_info?.categories?.includes("模板") ? "模板" : "元素材"
          }
          data-search-word={searchWord}
          data-pin-detail
          {...trackerData}
          onClick={() => {
            const downloadProcess = new DownloadProcess({
              pin,
              searchWord,
            });
            downloadProcess.processPinDetailEditOnline();
          }}
          theme="gray"
          icon={<HBIcon type="edit_new" />}
        >
          <span className={styles.editBtnText}>编辑</span>
        </HBButton>
      )}
    </div>
  );
}
